import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import { VictoryArea, VictoryAxis, VictoryChart, VictoryLine, VictoryScatter } from 'victory';
import dayjs from 'dayjs';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Select from 'common/components/select/Select';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useElementSize from 'common/hooks/useElementSize';
import Logger from 'common/services/Logger';
import Storage from 'common/services/Storage';
import Utils from 'common/services/Utils';
import { LOGGER_LOG_TYPE, DEFAULT_LANGUAGE, STORAGE } from 'Config';
import { ReactComponent as EcoIcon } from 'assets/svg/eco-conducao.svg';
import { DriverEcoComparisonDashboardDto } from 'api/dashboard/dtos/DriverEcoComparisonDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import WidgetNoData from '../components/WidgetNoData';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './DriverEcoComparison.module.scss';

type Props = {
	drivers: SelectValueLabel[];
};

const MIN_DRIVERS = 1;
const MAX_DRIVERS = 5;
const SELECTION_STORAGE_KEY = 'fleetmax_dashboard_driver_eco_comparison';

// Limiares alinhados com GetEcoDrivingRankingAsync no backend.
const ZONE_SAFE_MAX = 5;
const ZONE_WARNING_MAX = 15;

const ZONE_SAFE_COLOR = '#73C25C';
const ZONE_WARNING_COLOR = '#F3A70D';
const ZONE_DANGER_COLOR = '#D22222';

// Paleta para até 5 condutores — distinta das cores das bandas para evitar confusão visual.
const DRIVER_COLORS = ['#1781A1', '#6A4FBE', '#02c7c7', '#E26FA8', '#2D3A87'];

type ChartPoint = {
	x: Date;
	y: number;
	driverId: string;
	driverName: string;
};

type TooltipState = {
	monthKey: string;
	date: Date;
	x: number;
	y: number;
};

type MonthBreakdownItem = {
	driverId: string;
	driverName: string;
	lineColor: string;
	ecoValue: number;
};

// Mesma lógica de calcEcoColor de screens/vehicles-gps/components/ecoDriving/EcoDriving.tsx,
// mantida em sync para que os widgets partilhem o mesmo critério visual.
const calcEcoColor = (val: number) => {
	if (val <= 5) return ZONE_SAFE_COLOR;
	if (val > 5 && val < 15) return ZONE_WARNING_COLOR;
	return ZONE_DANGER_COLOR;
};

// O backend envia datas UTC à meia-noite do dia 1 (ex: "2025-09-01T00:00:00Z"). Em fusos horários
// a leste de UTC, `new Date(...)` mantém o instante UTC mas getMonth()/getDate() locais devolvem
// o mês anterior — por isso o eixo aparecia com "Ago" antes de Setembro. Esta helper extrai
// ano/mês em UTC e devolve uma Date local ao meio-dia do dia 15 desse mês, eliminando o drift.
const normalizeMonthDate = (raw: string | Date): Date => {
	const d = raw instanceof Date ? raw : new Date(raw);
	return new Date(d.getUTCFullYear(), d.getUTCMonth(), 15, 12, 0, 0);
};

const toMonthKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}`;

const DriverEcoComparison = ({ drivers }: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [selectedDrivers, setSelectedDrivers] = useState<SelectValueLabel[]>([]);
	const [data, setData] = useState<DriverEcoComparisonDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);
	const [hiddenDrivers, setHiddenDrivers] = useState<Record<string, boolean>>({});
	const [chartRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('driverEcoComparison');

	const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;

	const uniqueDrivers = useMemo(() => {
		const seen = new Set<string>();
		return drivers.filter(d => {
			if (seen.has(d.value)) return false;
			seen.add(d.value);
			return true;
		});
	}, [drivers]);

	useEffect(() => {
		if (uniqueDrivers.length === 0 || selectedDrivers.length > 0) return;
		try {
			const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
			if (!raw) return;
			const savedIds: string[] = JSON.parse(raw);
			const restored = savedIds
				.slice(0, MAX_DRIVERS)
				.map(id => uniqueDrivers.find(d => d.value === id))
				.filter((d): d is SelectValueLabel => d != null);
			if (restored.length >= MIN_DRIVERS) {
				setSelectedDrivers(restored);
			}
		} catch {
			// ignore
		}
		// eslint-disable-next-line
	}, [uniqueDrivers]);

	const fetchComparison = useCallback(async (driverIds: string[]) => {
		if (driverIds.length < MIN_DRIVERS) {
			setData([]);
			return;
		}
		try {
			setIsLoading(true);
			const result = await DashboardService.getDriverEcoComparison(driverIds, dateFrom, dateTo);
			setData(result || []);
		} catch (error) {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting driver eco comparison', error);
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line
	}, [dateFrom, dateTo]);

	useEffect(() => {
		const ids = selectedDrivers.map(d => d.value);
		fetchComparison(ids);
	}, [selectedDrivers, fetchComparison]);

	const handleDriverChange = (values: any) => {
		const selected: SelectValueLabel[] = values || [];
		if (selected.length > MAX_DRIVERS) return;
		setSelectedDrivers(selected);
		try {
			localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(selected.map(d => d.value)));
		} catch {
			// ignore
		}
	};

	// Mantém a ordem da selecção para que cor/legenda coincidam.
	const orderedData = useMemo(() => {
		if (!data.length) return [] as DriverEcoComparisonDashboardDto[];
		const ids = selectedDrivers.map(d => d.value);
		return ids
			.map(id => data.find(d => d.driverId === id))
			.filter((d): d is DriverEcoComparisonDashboardDto => d != null);
	}, [data, selectedDrivers]);

	const driverColorById = useMemo(() => {
		const map: Record<string, string> = {};
		orderedData.forEach((d, i) => {
			map[d.driverId] = DRIVER_COLORS[i % DRIVER_COLORS.length];
		});
		return map;
	}, [orderedData]);

	const visibleData = useMemo(() => {
		return orderedData.filter(d => !hiddenDrivers[d.driverId]);
	}, [orderedData, hiddenDrivers]);

	// Pontos por linha — datas convertidas para Date e valores nulos descartados.
	const seriesByDriver = useMemo(() => {
		return visibleData.map(driver => ({
			driver,
			points: driver.points
				.map((p): ChartPoint | null => {
					if (p.ecoValue == null) return null;
					return {
						x: normalizeMonthDate(p.date),
						y: Number(p.ecoValue),
						driverId: driver.driverId,
						driverName: driver.driverName,
					};
				})
				.filter((p): p is ChartPoint => p != null),
		}));
	}, [visibleData]);

	const allValues = useMemo(() => seriesByDriver.flatMap(s => s.points.map(p => p.y)), [seriesByDriver]);
	const maxY = useMemo(() => {
		const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0;
		// Garante que a banda vermelha (>=15) é sempre visível mesmo quando não há valores nessa zona.
		return Math.max(dataMax * 1.1, ZONE_WARNING_MAX + 5);
	}, [allValues]);

	// Domínio temporal do eixo X — se não houver dados ainda, usa a janela escolhida.
	const xDomain = useMemo<[Date, Date]>(() => {
		const allDates = orderedData.flatMap(d => d.points.map(p => normalizeMonthDate(p.date).getTime()));
		if (allDates.length > 0) {
			return [new Date(Math.min(...allDates)), new Date(Math.max(...allDates))];
		}
		return [dateFrom, dateTo];
	}, [orderedData, dateFrom, dateTo]);

	const tickValues = useMemo(() => {
		if (orderedData.length === 0) return [] as Date[];
		const seen = new Set<number>();
		const dates: Date[] = [];
		orderedData.forEach(d => {
			d.points.forEach(p => {
				const date = normalizeMonthDate(p.date);
				const t = date.getTime();
				if (!seen.has(t)) {
					seen.add(t);
					dates.push(date);
				}
			});
		});
		return dates.sort((a, b) => a.getTime() - b.getTime());
	}, [orderedData]);

	// Agregação por mês: para cada mês com pontos, lista todos os condutores visíveis nesse mês.
	// Usado para renderizar o tooltip em "coluna" ao estilo do ConsumesChart.
	const monthBreakdown = useMemo(() => {
		const map = new Map<string, MonthBreakdownItem[]>();
		visibleData.forEach(driver => {
			driver.points.forEach(p => {
				if (p.ecoValue == null) return;
				const date = normalizeMonthDate(p.date);
				const key = toMonthKey(date);
				const list = map.get(key) || [];
				list.push({
					driverId: driver.driverId,
					driverName: driver.driverName,
					lineColor: driverColorById[driver.driverId],
					ecoValue: Number(p.ecoValue),
				});
				map.set(key, list);
			});
		});
		return map;
	}, [visibleData, driverColorById]);

	const toggleDriver = (driverId: string) => {
		setHiddenDrivers(prev => ({ ...prev, [driverId]: !prev[driverId] }));
	};

	const scatterEvents = [{
		target: 'data' as const,
		eventHandlers: {
			onMouseEnter: (evt: any, targetProps: any) => {
				const d = targetProps.datum as ChartPoint;
				const date = d.x;
				setTooltip({
					monthKey: toMonthKey(date),
					date,
					x: evt.clientX,
					y: evt.clientY,
				});
				return [];
			},
			onMouseMove: (evt: any) => {
				setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
				return [];
			},
			onMouseLeave: () => { setTooltip(null); return []; },
		},
	}];

	const formatMonth = (date: Date) =>
		Utils.capitalize(dayjs(date).locale(locale).format('MMM')) + ' ' + dayjs(date).format('YY');

	const renderChart = () => {
		if (isLoading) {
			return <div className={styles.centerAlign}><LoadingSpinner /></div>;
		}
		if (selectedDrivers.length < MIN_DRIVERS) {
			return (
				<div className={styles.emptyMessage}>
					{t('dashboard.driver_eco_comparison_select_hint' as any, { max: MAX_DRIVERS })}
				</div>
			);
		}
		if (orderedData.length === 0 || allValues.length === 0) {
			return <WidgetNoData />;
		}
		if (width === 0 || height === 0) return null;

		// Bandas de fundo: cobrem toda a largura do gráfico.
		const [xMin, xMax] = xDomain;
		const safeBand = [
			{ x: xMin, y0: 0, y: ZONE_SAFE_MAX },
			{ x: xMax, y0: 0, y: ZONE_SAFE_MAX },
		];
		const warningBand = [
			{ x: xMin, y0: ZONE_SAFE_MAX, y: ZONE_WARNING_MAX },
			{ x: xMax, y0: ZONE_SAFE_MAX, y: ZONE_WARNING_MAX },
		];
		const dangerBand = [
			{ x: xMin, y0: ZONE_WARNING_MAX, y: maxY },
			{ x: xMax, y0: ZONE_WARNING_MAX, y: maxY },
		];

		return (
			<VictoryChart
				width={width}
				height={height}
				scale={{ x: 'time' }}
				padding={{ top: 15, bottom: 45, left: 50, right: 20 }}
				domain={{ y: [0, maxY] }}
				domainPadding={{ x: 25 }}
			>
				<VictoryArea
					data={safeBand}
					interpolation="linear"
					style={{ data: { fill: ZONE_SAFE_COLOR, fillOpacity: 0.13, stroke: 'transparent' } }}
				/>
				<VictoryArea
					data={warningBand}
					interpolation="linear"
					style={{ data: { fill: ZONE_WARNING_COLOR, fillOpacity: 0.13, stroke: 'transparent' } }}
				/>
				<VictoryArea
					data={dangerBand}
					interpolation="linear"
					style={{ data: { fill: ZONE_DANGER_COLOR, fillOpacity: 0.13, stroke: 'transparent' } }}
				/>
				<VictoryAxis
					tickValues={tickValues}
					tickFormat={(d: any) => formatMonth(new Date(d))}
					style={{
						axis: { stroke: 'transparent' },
						tickLabels: { fontSize: 11, fontFamily: 'Roboto', fill: '#6b7280' },
					}}
				/>
				<VictoryAxis
					dependentAxis
					style={{
						axis: { stroke: 'transparent' },
						grid: { stroke: '#f5f5f5' },
						tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
					}}
				/>
				{seriesByDriver.map(({ driver, points }) => (
					points.length > 0 && (
						<VictoryLine
							key={`line-${driver.driverId}`}
							data={points}
							x="x"
							y="y"
							interpolation="monotoneX"
							style={{
								data: {
									stroke: driverColorById[driver.driverId],
									strokeWidth: 2,
								},
							}}
						/>
					)
				))}
				{seriesByDriver.map(({ driver, points }) => (
					points.length > 0 && (
						<VictoryScatter
							key={`scatter-${driver.driverId}`}
							data={points}
							x="x"
							y="y"
							size={4}
							style={{
								data: {
									fill: driverColorById[driver.driverId],
									cursor: 'pointer',
								},
							}}
							// eslint-disable-next-line
							events={scatterEvents}
						/>
					)
				))}
			</VictoryChart>
		);
	};

	const selector = (
		<div className={styles.selectorWrapper}>
			<Select
				options={uniqueDrivers}
				value={selectedDrivers}
				onChange={handleDriverChange}
				isMulti
				isClearable
				slim
				usePortal
				placeholder={t('dashboard.driver_eco_comparison_select_placeholder' as any, { max: MAX_DRIVERS }) as string}
			/>
			<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
		</div>
	);

	const tooltipBreakdown = tooltip ? monthBreakdown.get(tooltip.monthKey) : null;
	// Ordena entradas do tooltip pelo eco_value (pior primeiro) para destacar quem precisa de atenção.
	const sortedTooltipBreakdown = useMemo(() => {
		if (!tooltipBreakdown) return null;
		return [...tooltipBreakdown].sort((a, b) => b.ecoValue - a.ecoValue);
	}, [tooltipBreakdown]);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.driver_eco_comparison' as any) as string}
			subTitleTooltip={t('dashboard.driver_eco_comparison_tooltip' as any) as string}
			renderOptions={selector}
		>
			<div className={styles.chartContainer}>
				<div className={styles.chartArea} ref={chartRef}>
					{renderChart()}
					{tooltip && sortedTooltipBreakdown && sortedTooltipBreakdown.length > 0 && (
						<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
							<div className={styles.tooltipTitle}>{formatMonth(tooltip.date)}</div>
							{sortedTooltipBreakdown.map(item => (
								<div key={item.driverId} className={styles.tooltipRow}>
									<span className={styles.tooltipRowLabel}>{item.driverName}</span>
									<span
										className={styles.tooltipRowValue}
										style={{ color: calcEcoColor(item.ecoValue) }}
									>
										{item.ecoValue.toFixed(2)}
									</span>
								</div>
							))}
						</ChartTooltipPortal>
					)}
				</div>
				{orderedData.length > 0 && (
					<>
						<div className={styles.customLegend}>
							{orderedData.map(driver => (
								<button
									key={driver.driverId}
									type="button"
									className={`${styles.legendItem} ${hiddenDrivers[driver.driverId] ? styles.legendItemOff : ''}`}
									onClick={() => toggleDriver(driver.driverId)}
								>
									<span
										className={styles.legendDot}
										style={{ background: driverColorById[driver.driverId] }}
									/>
									<span className={styles.legendLabel}>{driver.driverName}</span>
								</button>
							))}
						</div>
						<div
							className={styles.zonesLegend}
							data-tip={t('location.eco_penalty_label' as any) as string}
							data-for="driverEcoComparisonZonesTip"
						>
							<span className={styles.zonesLegendItem}>
								<EcoIcon className={styles.zonesLegendIcon} fill={ZONE_SAFE_COLOR} />
								{t('location.tooltip_eco_success', { until: 5 }) as string}
							</span>
							<span className={styles.zonesLegendItem}>
								<EcoIcon className={styles.zonesLegendIcon} fill={ZONE_WARNING_COLOR} />
								{t('location.tooltip_eco_warning', { from: 5.01, until: 14.99 }) as string}
							</span>
							<span className={styles.zonesLegendItem}>
								<EcoIcon className={styles.zonesLegendIcon} fill={ZONE_DANGER_COLOR} />
								{t('location.tooltip_eco_danger', { from: 15 }) as string}
							</span>
						</div>
						<ReactTooltip id="driverEcoComparisonZonesTip" effect="solid" />
					</>
				)}
			</div>
		</Box>
	);
};

export default DriverEcoComparison;
